/**
 * Turning the binding check into the sentences the Inbound card shows.
 *
 * Its own file, and pure, because the interesting part is not the rendering: it
 * is deciding WHICH of five problems the operator has, and how loudly to say so.
 * "No number points at us" is a red failure when the switch is on and a quiet
 * remark when it is off - the same data, two different meanings - and that rule
 * is worth being able to test without mounting a card.
 *
 * The server's half is `apps/api/src/modules/crm-calls/lib/binding.ts`.
 */
import type { NoteTone } from "../panel/fields";
import type { VoiceBindingResult } from "../../../../lib/calls";

export interface BindingNote {
  tone: NoteTone;
  /** i18n key under `settings.inbound.binding`. */
  key: string;
  vars?: Record<string, string | number>;
}

/**
 * What each error the check can report is called on screen.
 *
 * Named one by one rather than shown as a code: "the app sid does not exist in
 * this region" and "Twilio did not answer in ten seconds" are two different
 * afternoons. `not_configured` is absent on purpose - the card already says the
 * Twilio credentials are missing, and repeating it under a second heading would
 * make one problem look like two.
 */
const ERROR_NOTES: Record<string, { tone: NoteTone; key: string }> = {
  no_twiml_app: { tone: "warn", key: "noApp" },
  app_not_found: { tone: "bad", key: "appNotFound" },
  twilio_upstream: { tone: "warn", key: "upstream" },
  timeout: { tone: "warn", key: "timeout" },
};

/**
 * The verdict, worst news first.
 *
 * An empty list means there is nothing to say beyond the success line, which the
 * card renders itself from `numbers` - a check that passed should read as one
 * calm sentence, not five.
 */
export function bindingNotes(
  result: VoiceBindingResult,
  opts: { enabled: boolean; expectedUrl: string },
): BindingNote[] {
  if (result.error === "not_configured") return [];
  const known = result.error ? ERROR_NOTES[result.error] : undefined;
  if (known) return [known];
  // An unrecognised code still has to reach the operator rather than turn into
  // silence: a check that says nothing looks exactly like a check that passed.
  if (result.error) return [{ tone: "warn", key: "upstream" }];

  const notes: BindingNote[] = [];
  if (!result.appUrlMatches) {
    notes.push({
      tone: "bad",
      key: result.appVoiceUrl ? "urlMismatch" : "urlEmpty",
      vars: { found: result.appVoiceUrl, expected: opts.expectedUrl },
    });
  }
  if (!result.appMethodMatches) notes.push({ tone: "bad", key: "method" });
  if (result.numbers.length === 0) {
    // The whole point of the check. Red while the switch is on, because that is
    // the state where the page claims the phone is answered and it is not.
    notes.push({ tone: opts.enabled ? "bad" : "warn", key: opts.enabled ? "noNumbers" : "noNumbersOff" });
  } else if (result.truncated) {
    notes.push({ tone: "mut", key: "truncated" });
  }
  return notes;
}

/** "+372 5705 2008 (Husly CRM)" for each bound number, joined for one line. */
export function boundNumberLabel(row: { phoneNumber: string; friendlyName: string }): string {
  return row.friendlyName ? `${row.phoneNumber} (${row.friendlyName})` : row.phoneNumber;
}
